import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import SuperAdminLayout from '../components/SuperAdminLayout';
import { useCustomDialog } from '../context/DialogContext';
import '../styles/superadmin.css';

const BASE = 'https://menu.searchisend.in';

const SuperadminViewMenu = () => {
    const navigate = useNavigate();
    const { alert: customAlert, confirm: customConfirm } = useCustomDialog();
    const { customerId } = useParams();
    const superToken = localStorage.getItem('saas_superadmin_token');
    
    const [customer, setCustomer] = useState(null);
    const [customerToken, setCustomerToken] = useState(null);
    const [menuItems, setMenuItems] = useState([]);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        if (!superToken) {
            navigate('/superadmin_v2_secure_login');
            return;
        }
        initMenuAccess();
        // eslint-disable-next-line
    }, [customerId]);

    const initMenuAccess = async () => {
        setLoading(true);
        try {
            // 1. Get Customer details & Impersonation Token
            const res = await fetch(`${BASE}/apimenu/v2/v2_super_dashboard.php?action=generate_customer_token`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${superToken}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ customer_id: customerId })
            });
            const data = await res.json();
            
            if (data.success) {
                setCustomerToken(data.token);
                // Also fetch full customer list to get the exact restaurant name quickly
                const cRes = await fetch(`${BASE}/apimenu/v2/v2_super_dashboard.php?action=fetch_customers`, {
                    headers: { 'Authorization': `Bearer ${superToken}` }
                });
                const cData = await cRes.json();
                if (cData.success) {
                    const found = cData.customers.find(c => c.id.toString() === customerId);
                    setCustomer(found || data.user);
                } else {
                    setCustomer(data.user);
                }

                // 2. Fetch the menu using the customer token
                await fetchMenu(data.token);
            } else {
                await customAlert(data.message || 'Error authorizing menu access', 'Auth Error', 'danger');
                navigate('/superadmin_v2_secure');
            }
        } catch (err) {
            console.error('Initialization error', err);
        }
    };

    const fetchMenu = async (tokenToUse) => {
        try {
            const res = await fetch(`${BASE}/apimenu/v2/v2_manage_menu.php?action=fetch`, {
                headers: { 'Authorization': `Bearer ${tokenToUse}` }
            });
            const data = await res.json();
            if (data.success) {
                setMenuItems(data.items || []);
            }
        } catch (err) {
            console.error('Error fetching menu', err);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (itemId) => {
        if (!await customConfirm('Are you sure you want to delete this menu item?', 'Delete Menu Item', 'danger')) return;
        try {
            const res = await fetch(`${BASE}/apimenu/v2/v2_manage_menu.php?action=delete`, {
                method: 'POST',
                headers: { 
                    'Authorization': `Bearer ${customerToken}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ item_id: itemId })
            });
            const data = await res.json();
            if (data.success) {
                fetchMenu(customerToken);
            } else {
                await customAlert(data.message, 'Error', 'danger');
            }
        } catch (err) {
            await customAlert('Error deleting item', 'Error', 'danger');
        }
    };

    const handleImpersonateToManage = (path) => {
        localStorage.setItem('saas_admin_token', customerToken);
        localStorage.setItem('saas_admin_user', JSON.stringify({
            id: customer.id, 
            restaurant_name: customer.restaurant_name, 
            username: customer.username
        }));
        window.open(`/manage_v2_secure_auth${path}`, '_blank');
    };

    if (loading || !customer) {
        return (
            <div className="superadmin-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <div style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>
                    <i className="fas fa-circle-notch fa-spin fa-3x" style={{ color: 'var(--accent-primary)', marginBottom: '1rem' }}></i>
                    <p>Loading Menu Configuration...</p>
                </div>
            </div>
        );
    }

    // Group items by category
    const categories = menuItems.reduce((acc, item) => {
        if (!acc[item.category]) acc[item.category] = [];
        acc[item.category].push(item);
        return acc;
    }, {});

    return (
        <SuperAdminLayout>
            <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' }}>
                            <div>
                                <h2 className="sa-page-title">Menu for: {customer.restaurant_name}</h2>
                                <p className="sa-page-subtitle" style={{ margin: 0 }}>View and manage items across all categories.</p>
                            </div>
                            
                            <div style={{ display: 'flex', gap: '1rem' }}>
                                <button onClick={() => handleImpersonateToManage('/menu')} className="sa-primary-btn" style={{ background: 'var(--accent-primary)' }}>
                                    <i className="fas fa-plus"></i> Add New Item
                                </button>
                                <button onClick={() => handleImpersonateToManage('/menu')} className="sa-primary-btn" style={{ background: 'var(--info)', color: 'white' }}>
                                    <i className="fas fa-edit"></i> Bulk Edit Menu
                                </button>
                            </div>
                        </div>

                        {Object.keys(categories).length === 0 ? (
                            <div className="sa-panel" style={{ textAlign: 'center', padding: '4rem 2rem' }}>
                                <i className="fas fa-utensils fa-3x" style={{ color: 'var(--border-color)', marginBottom: '1rem' }}></i>
                                <p style={{ color: 'var(--text-secondary)', fontSize: '1.1rem' }}>This customer has no menu items yet.</p>
                                <button onClick={() => handleImpersonateToManage('/menu')} className="sa-primary-btn" style={{ marginTop: '1rem' }}>
                                    Create First Item
                                </button>
                            </div>
                        ) : (
                            Object.keys(categories).map(catName => (
                                <div key={catName} className="sa-panel" style={{ padding: 0, overflow: 'hidden', marginBottom: '2rem' }}>
                                    <div style={{ background: 'var(--bg-dark)', padding: '15px 20px', fontWeight: 700, fontSize: '1.1rem', color: 'var(--text-primary)', borderBottom: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <span><i className="fas fa-folder-open" style={{ color: 'var(--accent-primary)', marginRight: '10px' }}></i> {catName}</span>
                                        <span style={{ fontSize: '0.8rem', background: 'var(--bg-card)', padding: '4px 10px', borderRadius: '12px', color: 'var(--text-secondary)' }}>{categories[catName].length} items</span>
                                    </div>
                                    <div className="sa-table-container" style={{ margin: 0, border: 'none', borderRadius: 0, boxShadow: 'none' }}>
                                        <table className="sa-table" style={{ margin: 0 }}>
                                            <thead>
                                                <tr>
                                                    <th style={{ width: '60px' }}>ID</th>
                                                    <th style={{ width: '70px' }}>Image</th>
                                                    <th>Item Name</th>
                                                    <th>Sizes/Prices</th>
                                                    <th>Type</th>
                                                    <th style={{ textAlign: 'right' }}>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {categories[catName].map(item => (
                                                    <tr key={item.item_id}>
                                                        <td style={{ color: 'var(--text-secondary)', fontWeight: 500 }}>#{item.item_id}</td>
                                                        <td>
                                                            {item.image_url ? (
                                                                <img src={item.image_url} alt={item.name} style={{ width: '45px', height: '45px', objectFit: 'cover', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-color)' }} />
                                                            ) : (
                                                                <div style={{ width: '45px', height: '45px', background: 'var(--bg-dark)', borderRadius: 'var(--radius-sm)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary)' }}>
                                                                    <i className="fas fa-image"></i>
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{item.name}</td>
                                                        <td>
                                                            <span style={{ background: 'var(--bg-dark)', padding: '4px 8px', borderRadius: 'var(--radius-sm)', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                                                                {item.sizes ? item.sizes.length : item.size_count || 1} Size(s)
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <span style={{ 
                                                                display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '4px 10px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase',
                                                                background: item.item_type === 'veg' ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                                                                color: item.item_type === 'veg' ? 'var(--success)' : 'var(--danger)'
                                                            }}>
                                                                <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: item.item_type === 'veg' ? 'var(--success)' : 'var(--danger)' }}></span>
                                                                {item.item_type}
                                                            </span>
                                                        </td>
                                                        <td style={{ textAlign: 'right' }}>
                                                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                                                                <button onClick={() => handleImpersonateToManage('/menu')} className="sa-action-btn primary" title="Edit Item">
                                                                    <i className="fas fa-edit"></i> Edit
                                                                </button>
                                                                <button onClick={() => handleDelete(item.item_id)} className="sa-action-btn danger" title="Delete Item">
                                                                    <i className="fas fa-trash"></i>
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
        </SuperAdminLayout>
    );
};

export default SuperadminViewMenu;
